/**
 * sampleCsvImport — routes a validated sample CSV to one-or-many locations
 * (FEAT-2026-0017). Shared by LocationsPage and LocationsTabContent so the
 * grouping / auto-create / summary wording lives in exactly one place.
 *
 * Rules (defaults mirror the Work Order CSV import, TASK-007B):
 *  - a blank "Location ID" cell means "the location selected in the side panel";
 *  - a Location ID that matches an existing location in the project (by id, then
 *    by name, case-insensitive) files the row there;
 *  - an unknown Location ID is created on the fly as Type = Borehole with
 *    Name = the id (editable afterwards) — no DB change involved.
 * The server keeps its per-location bulk endpoint; we call it once per group.
 */
import { locationTypes } from '../../constants/statusOptions';
import { createLocation } from '../../services/locationService';
import { bulkCreateSamples, type BulkCreateResult } from '../../services/sampleService';
import type { CreateSampleRequest, Location } from '../../types/location';
import type { NormalizedSampleRow } from './csvUploadValidation';

/** Auto-created locations default to the first catalogued type ("Borehole"). */
const AUTO_CREATED_LOCATION_TYPE: string = locationTypes[0];

export interface LocationGroup {
  /** The id as it should read (first spelling seen in the file, or the selected id). */
  locationId: string;
  rows: NormalizedSampleRow[];
}

export interface LocationTargetSummary {
  /** Distinct target ids in file order (selected location first when used). */
  locationIds: string[];
  /** Ids that match no existing location and would be created on import. */
  newLocationIds: string[];
}

export interface SampleCsvImportOutcome {
  imported: number;
  failed: number;
  errors: { locationId: string; row: number; message: string }[];
  /** Distinct locations that received rows (existing + new). */
  locationCount: number;
  /** Locations created during this import, in creation order. */
  createdLocations: Location[];
}

const norm = (s: string): string => s.trim().toLowerCase();

/** The location a row targets: its own Location ID, or the selected one when blank. */
export function resolveLocationTarget(row: NormalizedSampleRow, selectedLocationId: string): string {
  return row.LocationId.trim() || selectedLocationId;
}

/** Group rows by target location (case-insensitive), preserving file order. */
export function groupRowsByLocation(
  rows: NormalizedSampleRow[],
  selectedLocationId: string,
): LocationGroup[] {
  const groups = new Map<string, LocationGroup>();
  for (const row of rows) {
    const target = resolveLocationTarget(row, selectedLocationId);
    const key = norm(target);
    const group = groups.get(key);
    if (group) group.rows.push(row);
    else groups.set(key, { locationId: target, rows: [row] });
  }
  return [...groups.values()];
}

/** Find an existing project location by id (preferred) or name, case-insensitive. */
export function findLocationByIdOrName(locations: Location[], locationId: string): Location | undefined {
  const key = norm(locationId);
  return locations.find((l) => norm(l.LocationId) === key)
    ?? locations.find((l) => norm(l.LocationName ?? '') === key);
}

/** Preview helper: which locations the file touches and which would be new. */
export function summarizeLocationTargets(
  rows: NormalizedSampleRow[],
  selectedLocationId: string,
  locations: Location[],
): LocationTargetSummary {
  const groups = groupRowsByLocation(rows, selectedLocationId);
  const locationIds = groups.map((g) => g.locationId);
  const newLocationIds = locationIds.filter((id) => !findLocationByIdOrName(locations, id));
  return { locationIds, newLocationIds };
}

/** The server's per-location endpoint does not know the Location ID column — strip it. */
function toCreateSampleRequest(row: NormalizedSampleRow): CreateSampleRequest {
  const { LocationId: _locationId, ...sample } = row;
  return sample;
}

export interface ImportSamplesParams {
  projectKey: string;
  rows: NormalizedSampleRow[];
  /** The location selected in the side panel — target for rows with a blank Location ID. */
  selectedLocation: Location;
  /** Every location currently known in the project (for matching). */
  locations: Location[];
}

/**
 * Import validated rows across locations: one bulk call per target location,
 * creating any location the project does not have yet. Groups are processed in
 * file order; a failure creating a location fails only that group's rows.
 */
export async function importSamplesAcrossLocations(params: ImportSamplesParams): Promise<SampleCsvImportOutcome> {
  const { projectKey, rows, selectedLocation, locations } = params;
  const known: Location[] = [...locations];
  const outcome: SampleCsvImportOutcome = {
    imported: 0, failed: 0, errors: [], locationCount: 0, createdLocations: [],
  };

  for (const group of groupRowsByLocation(rows, selectedLocation.LocationId)) {
    let target = findLocationByIdOrName(known, group.locationId);
    if (!target) {
      try {
        target = await createLocation(projectKey, {
          LocationId: group.locationId,
          LocationName: group.locationId,
          LocationType: AUTO_CREATED_LOCATION_TYPE,
        });
        known.push(target);
        outcome.createdLocations.push(target);
      } catch {
        outcome.failed += group.rows.length;
        outcome.errors.push({
          locationId: group.locationId, row: 0,
          message: `Could not create location '${group.locationId}' — ${group.rows.length} row(s) skipped`,
        });
        continue;
      }
    }

    const result: BulkCreateResult = await bulkCreateSamples(
      String(target.LocationKey),
      group.rows.map(toCreateSampleRequest),
    );
    outcome.imported += result.imported;
    outcome.failed += result.failed;
    outcome.locationCount += 1;
    for (const e of result.errors) {
      outcome.errors.push({ locationId: group.locationId, row: e.row, message: e.message });
    }
  }

  return outcome;
}

/** One-line snackbar wording for the outcome — shared so both pages read the same. */
export function describeImportOutcome(
  outcome: SampleCsvImportOutcome,
): { message: string; severity: 'success' | 'error' } {
  const where = outcome.locationCount > 1
    ? ` across ${outcome.locationCount} locations`
    : '';
  const created = outcome.createdLocations.length > 0
    ? ` · ${outcome.createdLocations.length} new location(s): ${outcome.createdLocations.map((l) => l.LocationId).join(', ')}`
    : '';
  if (outcome.failed > 0) {
    return {
      message: `${outcome.imported} imported${where}, ${outcome.failed} failed (duplicates or validation errors)${created}`,
      severity: outcome.imported > 0 ? 'success' : 'error',
    };
  }
  return { message: `${outcome.imported} sample(s) imported${where}${created}`, severity: 'success' };
}
